import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/become-distributor', label: 'Become a Distributor' },
    { path: '/about', label: 'About Us' },
    { path: '/contact', label: 'Contact' }
  ];

  const productCategories = [
    { path: '/shop/data-cables', label: 'Data Cables' },
    { path: '/shop/mobile-chargers', label: 'Mobile Chargers' },
    { path: '/shop/car-chargers', label: 'Car Chargers' },
    { path: '/shop/earbuds', label: 'Earbuds' },
    { path: '/shop/neckbands', label: 'Neckbands' },
    { path: '/shop', label: 'View All Products' },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-neutral-100' 
          : 'bg-white'
      }`}
    >
      {/* Top Bar */}
      <div className={`bg-gradient-to-r from-neutral-900 via-neutral-800 to-neutral-900 text-white py-2 transition-all duration-300 ${scrolled ? 'hidden' : 'block'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center text-xs">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              B2B Wholesale Only | Minimum Order Quantity Applies
            </span>
            <div className="hidden sm:flex items-center gap-4">
              <a href="tel:+919311211889" className="hover:text-[#FF4D00] transition-colors">
                +91-9311211889
              </a>
              <span className="text-neutral-500">|</span>
              <a href="mailto:contact@rsdaccessories.com" className="hover:text-[#FF4D00] transition-colors">
                contact@rsdaccessories.com
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <img 
              src="/images/logo.jpg" 
              alt="RSD Accessories Logo" 
              className="h-11 w-auto transition-transform group-hover:scale-105"
            />
            <div className="hidden sm:block">
              <span className="text-xl font-bold tracking-tight">
                <span className="text-neutral-900">R</span>
                <span className="text-[#FF4D00]">S</span>
                <span className="text-neutral-900">D A</span>
                <span className="text-[#FF4D00]">cc</span>
                <span className="text-neutral-900">essories</span>
              </span>
              <span className="block text-[10px] text-neutral-500 font-medium -mt-1">B2B Wholesale Only</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.slice(0, 1).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-[#FF4D00]/10 hover:text-[#FF4D00] ${
                  isActive(link.path) && link.path === '/' && location.pathname === '/' ? 'text-[#FF4D00] bg-[#FF4D00]/5' : 'text-neutral-700'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Products Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setProductsOpen(true)}
              onMouseLeave={() => setProductsOpen(false)}
            >
              <button
                className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-[#FF4D00]/10 hover:text-[#FF4D00] ${
                  location.pathname.startsWith('/shop') ? 'text-[#FF4D00] bg-[#FF4D00]/5' : 'text-neutral-700'
                }`}
              >
                Products
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${productsOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              <div className={`absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-xl border border-neutral-100 overflow-hidden transition-all duration-200 ${
                productsOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
              }`}>
                {productCategories.map((cat, idx) => (
                  <Link
                    key={cat.path}
                    to={cat.path}
                    className={`block px-4 py-3 text-sm font-medium transition-colors hover:bg-[#FF4D00]/10 hover:text-[#FF4D00] ${
                      idx === productCategories.length - 1 ? 'border-t border-neutral-100 bg-neutral-50 font-bold' : ''
                    } ${isActive(cat.path) ? 'text-[#FF4D00] bg-[#FF4D00]/5' : 'text-neutral-700'}`}
                  >
                    {cat.label}
                  </Link>
                ))}
              </div>
            </div>

            {navLinks.slice(1).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-[#FF4D00]/10 hover:text-[#FF4D00] ${
                  isActive(link.path) ? 'text-[#FF4D00] bg-[#FF4D00]/5' : 'text-neutral-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* CTA Button */}
            <a 
              href="tel:+919311211889"
              className="flex items-center gap-2 bg-gradient-to-r from-[#FF4D00] to-orange-500 text-white px-5 py-2.5 rounded-full text-sm font-bold hover:shadow-lg hover:shadow-[#FF4D00]/30 transition-all duration-300 hover:-translate-y-0.5 ml-2"
              data-testid="nav-call-btn"
            >
              <Phone className="w-4 h-4" />
              Request Quote
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center space-x-2">
            <a 
              href="tel:+919311211889"
              className="p-2.5 bg-gradient-to-r from-[#FF4D00] to-orange-500 text-white rounded-full shadow-lg"
            >
              <Phone className="w-5 h-5" />
            </a>
            
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ${
          mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div data-testid="mobile-menu" className="py-4 border-t border-neutral-200">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-3 px-4 text-sm font-medium transition-colors hover:bg-[#FF4D00]/10 rounded-lg mx-2 mb-1 ${
                  isActive(link.path) ? 'text-[#FF4D00] bg-[#FF4D00]/5' : 'text-neutral-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Mobile Product Categories */}
            <div className="px-4 py-2">
              <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider mb-2">Products</p>
              <div className="grid grid-cols-2 gap-2">
                {productCategories.slice(0, -1).map((cat) => (
                  <Link
                    key={cat.path}
                    to={cat.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="py-2 px-3 text-sm font-medium text-neutral-700 hover:text-[#FF4D00] hover:bg-[#FF4D00]/10 rounded-lg transition-colors"
                  >
                    {cat.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-4 px-4">
              <a 
                href="tel:+919311211889"
                className="block w-full text-center bg-gradient-to-r from-[#FF4D00] to-orange-500 text-white px-4 py-3 rounded-xl text-sm font-bold shadow-lg"
              >
                Call for Bulk Quote: +91-9311211889
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
